import { Box3, Mesh, Object3D, Vector3 } from 'three'

export type PlateCameraFocus = {
  bounds: Box3
  center: Vector3
  outwardNormal: Vector3
}

export function collectGLTFMeshes(scene: Object3D) {
  scene.updateWorldMatrix(true, true)
  const meshes: Mesh[] = []
  scene.traverse((node) => {
    if (node instanceof Mesh) meshes.push(node)
  })
  const markedShells = meshes.filter(
    (mesh) => mesh.userData.role === 'shell-plate'
  )
  const surfaces = markedShells.length
    ? markedShells
    : meshes.filter((mesh) => mesh.name === 'Interactive_Object')
  const surfaceSet = new Set(surfaces)
  return {
    surfaces,
    context: meshes.filter((mesh) => !surfaceSet.has(mesh)),
    bbox: new Box3().setFromObject(scene),
    usesMarkedShells: markedShells.length > 0,
  }
}

export function isSelectedPlateMesh(
  mesh: Mesh,
  selectedPlateID: string | undefined
) {
  return (
    selectedPlateID !== undefined &&
    mesh.userData.role === 'shell-plate' &&
    mesh.userData.plate_id === selectedPlateID
  )
}

export function getPlateCameraFocus(
  surfaces: Mesh[],
  selectedPlateID: string,
  modelBounds: Box3
): PlateCameraFocus | undefined {
  const mesh = surfaces.find((surface) =>
    isSelectedPlateMesh(surface, selectedPlateID)
  )
  if (!mesh) return undefined

  mesh.updateWorldMatrix(true, false)
  const bounds = new Box3().setFromObject(mesh)
  if (bounds.isEmpty()) return undefined

  const center = bounds.getCenter(new Vector3())
  const modelCenter = modelBounds.getCenter(new Vector3())
  const outwardReference = center.clone().sub(modelCenter)
  const outwardNormal = areaWeightedOutwardNormal(mesh, outwardReference)

  return { bounds, center, outwardNormal }
}

function areaWeightedOutwardNormal(mesh: Mesh, outwardReference: Vector3) {
  const position = mesh.geometry.getAttribute('position')
  const index = mesh.geometry.getIndex()
  const normal = new Vector3()
  const a = new Vector3()
  const b = new Vector3()
  const c = new Vector3()
  const ab = new Vector3()
  const ac = new Vector3()
  const triangleCount = index
    ? Math.floor(index.count / 3)
    : Math.floor(position.count / 3)

  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const offset = triangle * 3
    const aIndex = index?.getX(offset) ?? offset
    const bIndex = index?.getX(offset + 1) ?? offset + 1
    const cIndex = index?.getX(offset + 2) ?? offset + 2
    a.fromBufferAttribute(position, aIndex).applyMatrix4(mesh.matrixWorld)
    b.fromBufferAttribute(position, bIndex).applyMatrix4(mesh.matrixWorld)
    c.fromBufferAttribute(position, cIndex).applyMatrix4(mesh.matrixWorld)
    ab.subVectors(b, a)
    ac.subVectors(c, a)
    const faceNormal = ab.cross(ac)
    if (faceNormal.dot(outwardReference) < 0) faceNormal.negate()
    normal.add(faceNormal)
  }

  if (normal.lengthSq() > 1e-12) return normal.normalize()
  if (outwardReference.lengthSq() > 1e-12) return outwardReference.normalize()
  return new Vector3(0, 0, 1)
}
